import { useState } from 'react';
import { toast } from 'sonner';
import salesApiClient from '@/api/salesClient';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle, Loader2, Table, FileDown } from 'lucide-react';

export default function DataImport() {
  const [selectedType, setSelectedType] = useState('leads');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const dataTypes = [
    { value: 'leads', label: 'Leads', description: 'Import leads with contact info, value, and stage' },
    { value: 'contacts', label: 'Contacts', description: 'Import contacts with name, company, email, phone' },
    { value: 'tasks', label: 'Tasks', description: 'Import tasks with title, due date, priority' },
    { value: 'contracts', label: 'Contracts', description: 'Import contracts with value, duration, staff count' }
  ];

  const handleDownloadTemplate = async (type) => {
    try {
      const response = await salesApiClient.get(`/import/template/${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mysales_orbit_${type}_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setPreview(null);
      setImportResult(null);
    }
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setPreviewing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('data_type', selectedType);

      const response = await salesApiClient.post('/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPreview(response.data);
      
      if (!response.data.columns_match) {
        toast.warning('Column mismatch detected - please check the preview');
      } else {
        toast.success('File preview loaded successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview file');
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!file || !preview) {
      toast.error('Please preview the file first');
      return;
    }

    if (!preview.columns_match) {
      toast.error('Cannot import - column headers do not match the template');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('data_type', selectedType);

      const response = await salesApiClient.post('/import/execute', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      
      if (response.data.imported_count > 0) {
        toast.success(`Successfully imported ${response.data.imported_count} ${selectedType}`);
      }
      
      if (response.data.total_errors > 0) {
        toast.warning(`${response.data.total_errors} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await salesApiClient.get(`/export/${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mysales_orbit_${type}_export.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`);
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 space-y-8 fade-in">
      <div>
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white">
          Data Import & Export
        </h1>
        <p className="text-base text-slate-400 mt-2">
          Import data from Excel or export your existing data
        </p>
      </div>

      {/* Export Section */}
      <Card className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-green-500/20 p-2 rounded-lg">
            <FileDown size={24} className="text-green-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Export Data</h2>
            <p className="text-sm text-slate-400">Download your existing data as Excel files</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => handleExport('leads')}>
            <Download size={16} className="mr-2" />
            Export Leads
          </Button>
          <Button variant="outline" onClick={() => handleExport('contacts')}>
            <Download size={16} className="mr-2" />
            Export Contacts
          </Button>
          <Button variant="outline" onClick={() => handleExport('tasks')}>
            <Download size={16} className="mr-2" />
            Export Tasks
          </Button>
          <Button variant="outline" onClick={() => handleExport('contracts')}>
            <Download size={16} className="mr-2" />
            Export Contracts
          </Button>
          <Button onClick={() => handleExport('all')}>
            <Download size={16} className="mr-2" />
            Export All Data
          </Button>
        </div>
      </Card>

      {/* Import Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Step 1: Select Data Type */}
        <Card className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">1</div>
            <h2 className="text-lg font-semibold text-white">Select Data Type</h2>
          </div>
          <div className="space-y-3">
            {dataTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => {
                  setSelectedType(type.value);
                  resetForm();
                }}
                className={`w-full text-left p-4 rounded-lg border transition-all ${
                  selectedType === type.value
                    ? 'border-primary bg-primary/20 text-white'
                    : 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{type.label}</span>
                  {selectedType === type.value && (
                    <CheckCircle2 size={20} className="text-primary" />
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-1">{type.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleDownloadTemplate(selectedType)}
            >
              <FileSpreadsheet size={16} className="mr-2" />
              Download {selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} Template
            </Button>
          </div>
        </Card>

        {/* Step 2: Upload File */}
        <Card className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">2</div>
            <h2 className="text-lg font-semibold text-white">Upload Excel File</h2>
          </div>
          
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
              file ? 'border-green-500 bg-green-500/10' : 'border-slate-600 hover:border-slate-500'
            }`}
          >
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              {file ? (
                <div className="space-y-2">
                  <CheckCircle2 size={48} className="mx-auto text-green-400" />
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-sm text-slate-400">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <p className="text-xs text-green-400">Click to change file</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload size={48} className="mx-auto text-slate-500" />
                  <p className="text-slate-300">Click to upload Excel file</p>
                  <p className="text-xs text-slate-500">.xlsx or .xls files only</p>
                </div>
              )}
            </label>
          </div>

          <div className="mt-4 space-y-3">
            <Button
              className="w-full"
              onClick={handlePreview}
              disabled={!file || previewing}
            >
              {previewing ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Loading Preview...
                </>
              ) : (
                <>
                  <Table size={16} className="mr-2" />
                  Preview Data
                </>
              )}
            </Button>
            
            {preview && (
              <Button
                className="w-full"
                variant={preview.columns_match ? "default" : "destructive"}
                onClick={handleImport}
                disabled={importing || !preview.columns_match}
              >
                {importing ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload size={16} className="mr-2" />
                    Import {preview.total_rows} Rows
                  </>
                )}
              </Button>
            )}
          </div>
        </Card>

        {/* Step 3: Preview & Results */}
        <Card className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white font-bold">3</div>
            <h2 className="text-lg font-semibold text-white">Preview & Results</h2>
          </div>

          {!preview && !importResult && (
            <div className="text-center py-12 text-slate-500">
              <FileSpreadsheet size={48} className="mx-auto mb-4 opacity-50" />
              <p>Upload and preview a file to see data here</p>
            </div>
          )}

          {preview && !importResult && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {preview.columns_match ? (
                  <CheckCircle2 size={20} className="text-green-400" />
                ) : (
                  <XCircle size={20} className="text-red-400" />
                )}
                <span className={preview.columns_match ? 'text-green-400' : 'text-red-400'}>
                  {preview.columns_match ? 'Columns match template' : 'Column mismatch'}
                </span>
              </div>

              <div className="bg-slate-800 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Total rows:</span>
                  <span className="text-white font-medium">{preview.total_rows}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Columns found:</span>
                  <span className="text-white font-medium">{preview.columns.length}</span>
                </div>
              </div>

              {preview.missing_columns.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3">
                  <p className="text-red-400 text-sm font-medium mb-1">Missing Columns:</p>
                  <p className="text-red-300 text-xs">
                    {preview.missing_columns.join(', ')}
                  </p>
                </div>
              )}

              {preview.extra_columns.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/50 rounded-lg p-3">
                  <p className="text-amber-400 text-sm font-medium mb-1">Extra Columns (ignored):</p>
                  <p className="text-amber-300 text-xs">
                    {preview.extra_columns.join(', ')}
                  </p>
                </div>
              )}

              <div className="text-xs text-slate-500">
                Showing preview of first {preview.preview_rows} rows
              </div>
            </div>
          )}

          {importResult && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {importResult.imported_count > 0 ? (
                  <CheckCircle2 size={24} className="text-green-400" />
                ) : (
                  <AlertTriangle size={24} className="text-amber-400" />
                )}
                <span className="text-lg font-semibold text-white">
                  Import Complete
                </span>
              </div>

              <div className="bg-slate-800 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Successfully imported:</span>
                  <span className="text-green-400 font-bold">{importResult.imported_count}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Total rows processed:</span>
                  <span className="text-white font-medium">{importResult.total_rows}</span>
                </div>
                {importResult.total_errors > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Rows with errors:</span>
                    <span className="text-red-400 font-medium">{importResult.total_errors}</span>
                  </div>
                )}
              </div>

              {importResult.errors && importResult.errors.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <p className="text-red-400 text-sm font-medium mb-2">Errors:</p>
                  {importResult.errors.map((error, idx) => (
                    <p key={idx} className="text-red-300 text-xs mb-1">{error}</p>
                  ))}
                </div>
              )}

              <Button variant="outline" className="w-full" onClick={resetForm}>
                Import More Data
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Data Preview Table */}
      {preview && preview.data && preview.data.length > 0 && (
        <Card className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Data Preview</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  {preview.columns.map((col, idx) => (
                    <th key={idx} className="text-left p-2 text-slate-400 font-medium">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.data.slice(0, 10).map((row, rowIdx) => (
                  <tr key={rowIdx} className="border-b border-slate-800 hover:bg-slate-800/50">
                    {preview.columns.map((col, colIdx) => (
                      <td key={colIdx} className="p-2 text-slate-300 truncate max-w-[200px]">
                        {String(row[col] || '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.total_rows > 10 && (
            <p className="text-xs text-slate-500 mt-4 text-center">
              Showing 10 of {preview.total_rows} rows
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
